/**
 * Follow system — readers/writers follow each other via RTDB graph:
 * - `users/{followerId}/following/{followedId}` (follower maintains)
 * - `users/{followedId}/followers/{followerId}` (follower maintains; rules allow only that edge)
 *
 * Display counts: prefer `aggregates/*Count` (maintained on follow/unfollow), then list length from `followers` / `following`.
 */

import { ref, set, get, remove, runTransaction } from "firebase/database";
import { db } from "../auth/firebase";
import { getPublicCatalog } from "../features/content/contentCrudService";
import { filterFollowingFeed } from "./followingFeed";

async function applyFollowAggregateDelta(followedId, followerId, delta) {
  try {
    await Promise.all([
      runTransaction(ref(db, `users/${followedId}/aggregates/followersCount`), (cur) => {
        const n = typeof cur === "number" && !Number.isNaN(cur) ? cur : 0;
        return Math.max(0, n + delta);
      }),
      runTransaction(ref(db, `users/${followerId}/aggregates/followingCount`), (cur) => {
        const n = typeof cur === "number" && !Number.isNaN(cur) ? cur : 0;
        return Math.max(0, n + delta);
      }),
    ]);
  } catch (e) {
    console.warn("followService: aggregate counter update failed (graph is still correct)", e?.message || e);
  }
}

/**
 * Follow a user
 */
export const followUser = async (followerId, followingId, followerName = null) => {
  try {
    if (followerId === followingId) {
      throw new Error("Cannot follow yourself");
    }

    const followingRef = ref(db, `users/${followerId}/following/${followingId}`);
    await set(followingRef, {
      userId: followingId,
      followedAt: new Date().toISOString(),
    });

    const followersRef = ref(db, `users/${followingId}/followers/${followerId}`);
    await set(followersRef, {
      userId: followerId,
      followedAt: new Date().toISOString(),
    });

    await applyFollowAggregateDelta(followingId, followerId, 1);

    try {
      if (followerName) {
        const { notifyFollow } = await import("./notificationService");
        await notifyFollow(followingId, followerName, followerId);
      }
    } catch (notifError) {
      console.error("Error sending follow notification:", notifError);
    }

    return { success: true };
  } catch (error) {
    console.error("Error following user:", error);
    throw error;
  }
};

/**
 * Unfollow a user
 */
export const unfollowUser = async (followerId, followingId) => {
  try {
    await applyFollowAggregateDelta(followingId, followerId, -1);

    const followingRef = ref(db, `users/${followerId}/following/${followingId}`);
    await remove(followingRef);

    const followersRef = ref(db, `users/${followingId}/followers/${followerId}`);
    await remove(followersRef);

    return { success: true };
  } catch (error) {
    console.error("Error unfollowing user:", error);
    throw error;
  }
};

/**
 * Check if user is following another user
 */
export const isFollowing = async (followerId, followingId) => {
  try {
    const followingRef = ref(db, `users/${followerId}/following/${followingId}`);
    const snapshot = await get(followingRef);
    return snapshot.exists();
  } catch (error) {
    console.error("Error checking follow status:", error);
    return false;
  }
};

/**
 * Get all users that a user is following
 */
export const getFollowing = async (userId) => {
  try {
    const followingRef = ref(db, `users/${userId}/following`);
    const snapshot = await get(followingRef);

    if (!snapshot.exists()) {
      return [];
    }

    const following = [];
    snapshot.forEach((childSnapshot) => {
      following.push({
        userId: childSnapshot.key,
        ...childSnapshot.val(),
      });
    });

    return following;
  } catch (error) {
    console.error("Error getting following:", error);
    return [];
  }
};

/**
 * Writers the reader follows, with display names from `users/{id}` for list UI.
 * @param {string} readerUserId
 * @returns {Promise<{ userId: string, followedAt: string|null, displayName: string }[]>}
 */
export const getFollowingWritersEnriched = async (readerUserId) => {
  if (!readerUserId) return [];
  const edges = await getFollowing(readerUserId);
  if (!edges.length) return [];

  const rows = await Promise.all(
    edges.map(async (edge) => {
      const uid = String(edge.userId || "").trim();
      if (!uid) return null;
      try {
        const snap = await get(ref(db, `publicProfiles/${uid}`));
        const p = snap.exists() ? snap.val() || {} : {};
        const displayName =
          String(p.displayName || p.authorName || "").trim() || "Writer";
        return {
          userId: uid,
          followedAt: edge.followedAt || null,
          displayName,
        };
      } catch {
        return { userId: uid, followedAt: edge.followedAt || null, displayName: "Writer" };
      }
    })
  );
  return rows.filter(Boolean).sort((a, b) => {
    const ta = a.followedAt ? new Date(a.followedAt).getTime() : 0;
    const tb = b.followedAt ? new Date(b.followedAt).getTime() : 0;
    return tb - ta;
  });
};

/**
 * Get all followers of a user
 */
export const getFollowers = async (userId) => {
  try {
    const followersRef = ref(db, `users/${userId}/followers`);
    const snapshot = await get(followersRef);

    if (!snapshot.exists()) {
      return [];
    }

    const followers = [];
    snapshot.forEach((childSnapshot) => {
      followers.push({
        userId: childSnapshot.key,
        ...childSnapshot.val(),
      });
    });

    return followers;
  } catch (error) {
    console.error("Error getting followers:", error);
    return [];
  }
};

function readAggregateNumber(snapshot) {
  if (!snapshot?.exists()) return null;
  const v = snapshot.val();
  return typeof v === "number" && !Number.isNaN(v) ? v : null;
}

/**
 * Get follower count for a user (aggregates + graph; max of both handles legacy data).
 */
export const getFollowerCount = async (userId) => {
  if (!userId) return 0;
  let agg = null;
  let graph = 0;
  try {
    const snap = await get(ref(db, `users/${userId}/aggregates/followersCount`));
    agg = readAggregateNumber(snap);
  } catch (error) {
    console.warn("getFollowerCount aggregate", userId, error?.code || error?.message);
  }
  try {
    graph = (await getFollowers(userId)).length;
  } catch (error) {
    console.warn("getFollowerCount graph", userId, error?.code || error?.message);
  }
  return Math.max(agg ?? 0, graph);
};

/**
 * Get following count for a user
 */
export const getFollowingCount = async (userId) => {
  if (!userId) return 0;
  let agg = null;
  let graph = 0;
  try {
    const snap = await get(ref(db, `users/${userId}/aggregates/followingCount`));
    agg = readAggregateNumber(snap);
  } catch (error) {
    console.warn("getFollowingCount aggregate", userId, error?.code || error?.message);
  }
  try {
    graph = (await getFollowing(userId)).length;
  } catch (error) {
    console.warn("getFollowingCount graph", userId, error?.code || error?.message);
  }
  return Math.max(agg ?? 0, graph);
};

/**
 * Backfill `aggregates/*Count` from the follow graph (best effort). Safe to call after deploy;
 * uses the same reads as getFollowers/getFollowing.
 */
export const syncFollowAggregatesFromGraph = async (userId) => {
  if (!userId) return;
  try {
    const [followersLen, followingLen] = await Promise.all([
      getFollowers(userId).then((a) => a.length),
      getFollowing(userId).then((a) => a.length),
    ]);
    await Promise.all([
      set(ref(db, `users/${userId}/aggregates/followersCount`), followersLen),
      set(ref(db, `users/${userId}/aggregates/followingCount`), followingLen),
    ]);
  } catch (e) {
    console.warn("syncFollowAggregatesFromGraph", userId, e?.message || e);
  }
};

/**
 * Latest published public pieces from followed writers.
 * Uses the public visibility catalog. Listing another user's `authorId` is denied by rules.
 * @param {string} userId - signed-in reader
 * @param {string|null} contentType - explore slug (e.g. `articles`) or null for all
 * @param {object|null} viewerUser - Redux user for visibility checks
 */
export const getFollowingFeed = async (userId, contentType = null, viewerUser = null) => {
  const following = await getFollowing(userId);
  if (following.length === 0) return [];
  const followingIds = [...new Set(following.map((f) => f.userId).filter(Boolean))];
  const rows = await getPublicCatalog(500);
  return filterFollowingFeed(rows, followingIds, { contentType, viewerUser });
};
